import { useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Subject } from 'rxjs';

import { useAuth } from '@/api-hooks/useAuth';
import AppApi from '@/apis/app-api';
import {
  BatchReplyV3CreateResult,
  BatchReplyV3Delta,
  BatchReplyV3SnapshotDto,
  BatchReplyV3StatusDto,
  BatchRepliesV3Api,
  batchReplyV3StreamUrl,
  CreateBatchReplyV3Body,
  isTerminalV3Status,
} from '@/apis/batch-replies-v3.api';
import { baseUrl } from '@/consts/route';
import { useEnqueueIslandEvents } from '@/features/BatchReplyV3/context';
import { isIslandMockEnabled } from '@/features/BatchReplyV3/mockIslandFlag';
import {
  patchDetailReplyStatus,
  patchReviewReplyStatus,
  reconcileDetailFromSnapshot,
  reconcileFromSnapshot,
} from '@/lib/batchReplyV3Cache';
import { share } from '@/lib/connection/shared-connection';
import { SSEConnection } from '@/lib/connection/sse-connection';
import { RealtimeLiveQuery, RealtimeSubscriptionReady } from '@/lib/realtime-live-query';

const makeApi = (accessToken: string) => {
  const api = new AppApi(baseUrl);
  api.setup(accessToken);
  return new BatchRepliesV3Api(api);
};

/** axios/HTTP 에러가 404(NotFound)인지 */
const isNotFoundError = (err: unknown): boolean => {
  const e = err as { response?: { status?: number }; status?: number };
  return (e?.response?.status ?? e?.status) === 404;
};

/**
 * axios/HTTP 에러가 400(BadRequest)인지 — create 시 "선택 리뷰가 모두 이미 처리 중/완료라 대상 없음"
 * (BE: BadRequestException). FE는 빈 내용/진행중 job을 미리 가드하므로, create 400은 사실상 이 케이스다.
 */
export const isBadRequestError = (err: unknown): boolean => {
  const e = err as { response?: { status?: number }; status?: number };
  return (e?.response?.status ?? e?.status) === 400;
};

// job 조회 실패(404/스트림 error) 시 사용자에게 보일 친화적 카피 (BE raw message는 노출하지 않음)
const JOB_GONE_MESSAGE = '일괄댓글 정보를 불러올 수 없어요.';

/**
 * status/detail/snapshot이 404(존재X 또는 소유권 불일치)로 확인된 jobId denylist.
 * 재구독/재폴링/재채택을 막아 무한 404를 차단한다. ⚠️ 근본 원인은 BE 인가 불일치
 * (active-status는 reply.user_id로 찾지만 status/detail/snapshot은 payload.user_id로 검증 →
 *  둘이 어긋난 유령 job이 active엔 뜨는데 나머진 404). BE 정합이 정답이고, 이건 FE 회복탄력성.
 */
const deadBatchJobIds = new Set<string>();
export const isDeadBatchJob = (jobId: string | null | undefined): boolean =>
  !!jobId && deadBatchJobIds.has(jobId);
const markDeadBatchJob = (jobId: string | null | undefined) => {
  if (jobId) deadBatchJobIds.add(jobId);
};

/**
 * 사용자가 배너/위젯에서 "닫기"로 dismiss한 job — 이번 세션에서 다시 추적/재채택하지 않는다.
 * (특히 terminal-non-COMPLETED[FAILED/CANCELLED/EXPIRED]는 BE confirm 실패 시에도 active-status가
 *  24h 반환하므로, 재채택을 막아 유령 배너 재등장을 방지. dead-set 재사용.)
 */
export const dismissBatchJob = (jobId: string | null | undefined) => markDeadBatchJob(jobId);

/** 404엔 재시도 금지(유령 job 3x 스팸 방지), 그 외엔 기본 2회 */
const retryExcept404 = (failureCount: number, error: unknown) =>
  !isNotFoundError(error) && failureCount < 2;

// ============================================
// Query Keys
// ============================================
export const batchReplyV3Keys = {
  all: ['batch_reply_v3'] as const,
  status: (jobId: string | null) => [...batchReplyV3Keys.all, 'status', jobId] as const,
  detail: (jobId: string | null) => [...batchReplyV3Keys.all, 'detail', jobId] as const,
  activeStatus: () => [...batchReplyV3Keys.all, 'active-status'] as const,
};

type MutationOpts<TData, TVars> = Omit<
  UseMutationOptions<TData, Error, TVars>,
  'mutationFn' | 'mutationKey'
>;

// ============================================
// 생성
// ============================================
export const useCreateBatchReplyV3 = (
  options?: MutationOpts<BatchReplyV3CreateResult, CreateBatchReplyV3Body>,
) => {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...batchReplyV3Keys.all, 'create'],
    mutationFn: (body: CreateBatchReplyV3Body) => makeApi(accessToken).create(body),
    ...options,
    // ⚠️ 무거운 ['reviews'] 통째 invalidate 금지 — 낙관적 promote + delta patch로 대체.
    onSettled: (...args) => {
      queryClient.invalidateQueries({ queryKey: batchReplyV3Keys.all });
      options?.onSettled?.(...args);
    },
  });
};

// ============================================
// 상태 조회 (읽기 전용 — transport는 Live가 단독 소유)
// ============================================
export const useBatchReplyV3Status = (jobId: string | null) => {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: batchReplyV3Keys.status(jobId),
    queryFn: () => makeApi(accessToken).getStatus(jobId as string),
    // mock 미리보기일 땐 실제 GET 없이 seed된 캐시만 사용. 죽은 job은 조회 안 함(무한 404 방지)
    enabled: !!accessToken && !!jobId && !isDeadBatchJob(jobId) && !isIslandMockEnabled(),
    // #4 소량 staleTime — 신선도는 SSE snapshot(setQueryData)이 담당하므로 staleTime:0일 필요 없음.
    // 여러 위젯의 재마운트/focus가 /status를 연타(remount 폭풍)하지 못하게 3s 완충.
    staleTime: 3000,
    refetchInterval: false,
    retry: retryExcept404,
    // ⚠️ 모노토닉 가드: 실제 job은 terminal→non-terminal로 역행하지 않는다. status 캐시는
    // getStatus(쿼리) / SSE applySnapshot(setQueryData) / getSnapshot(폴백) 3개가 generation 조율 없이
    // 쓴다. terminal 도착 후 in-flight 였던 stale getStatus가 IN_PROGRESS로 되돌리면(clobber) 완료 카드가
    // RunningCard로 튀고 liveJobId 게이트가 진동해 SSE가 재연결 폭풍을 일으킨다. terminal→non-terminal
    // 회귀만 막아 그 clobber를 무력화한다(정상 진행 갱신은 그대로 통과).
    structuralSharing: (prev, next) => {
      const p = prev as BatchReplyV3StatusDto | undefined;
      const n = next as BatchReplyV3StatusDto;
      if (p && isTerminalV3Status(p.status) && !isTerminalV3Status(n.status)) return p;
      return n;
    },
  });
};

// 등록 결과/현황 상세 (리뷰 본문 + 건별 상태) — 결과 화면 & 진행중 현황 화면 공용.
// 한 번만 fetch(본문 스냅샷). 진행중 건별 ✓/❗ 라이브 갱신은 V3StreamSubscriber의 SSE delta가
// detail 캐시를 낙관적 patch(patchDetailReplyStatus)하고, 놓친 건 18s snapshot backstop이 reconcile.
// → 폴링 없음(AFFiNE: 이벤트로 낙관적 업데이트 + snapshot fallback).
export const useBatchReplyV3Detail = (jobId: string | null, enabled = true) => {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: batchReplyV3Keys.detail(jobId),
    queryFn: () => makeApi(accessToken).getDetail(jobId as string),
    // mock 미리보기일 땐 실제 GET 없이 seed된 캐시만 사용. 죽은 job은 조회 안 함
    enabled:
      !!accessToken && !!jobId && enabled && !isDeadBatchJob(jobId) && !isIslandMockEnabled(),
    staleTime: 0,
    retry: retryExcept404,
  });
};

// ============================================
// 진입 시 활성 작업 복원 (24h 내 미확인 job, 없으면 null)
// ============================================
export const useBatchReplyV3ActiveStatus = (enabled = true) => {
  const { accessToken } = useAuth();

  return useQuery({
    queryKey: batchReplyV3Keys.activeStatus(),
    queryFn: () => makeApi(accessToken).getActiveStatus(),
    enabled: !!accessToken && enabled,
    // ⚠️ staleTime:0이면 다중 observer(위젯들 + 버튼 가드) + focus/mount refetch가 폭주하고,
    // BE eventual-consistency로 일시적 job_id=null을 자주 만나 adoption effect의 clear()가 위젯을
    // 껐다 켰다 하는 flap을 유발한다. 신선도는 create/confirm onSettled invalidate가 보장하므로 10s 완충.
    staleTime: 10_000,
  });
};

/**
 * 단일 실행 가드 — "지금 추적 중인 job이 있는가"의 authority는 **서버 active-status**다.
 * localStorage jobId(optimistic 캐시)를 보면 stale/mock 값(예: 확인 안 하고 남은 'mock-…',
 * 24h 지나 서버가 이미 정리한 job)에 걸려 진입이 막히므로, AFFiNE 원칙(서버=truth)대로 서버에 묻는다.
 * - active-status는 CONFIRMED가 아닌 최신 job(진행중/완료-미확인 포함)을 반환 → 있으면 "1개 점유".
 * - mock 미리보기에선 가드 해제(클릭이 mock 시퀀스를 구동해야 하므로).
 * (같은 queryKey라 V3StreamSubscriber가 이미 마운트한 쿼리를 공유 — 추가 요청 없음)
 */
export const useHasActiveBatchReplyJob = (): boolean => {
  const mock = isIslandMockEnabled();
  const { data: active } = useBatchReplyV3ActiveStatus(!mock);
  const jobId = isDeadBatchJob(active?.job_id) ? null : (active?.job_id ?? null);
  // 서버가 active로 보고 + status가 실제 로드되는(=추적 가능한) job만 "슬롯 점유"로 본다.
  // active-status는 reply.user_id 조인으로 찾지만 status/detail은 flowJob.payload.user_id로 검증 →
  // 둘이 어긋난 stale/불일치 job이면 active엔 뜨는데 status는 404. 그때 블록하면 진입 위젯이
  // 못 뜨는 채 클릭만 삼켜 막다른 길이 되므로, status가 로드된(트래킹 가능한) 경우에만 블록한다.
  const { data: status } = useBatchReplyV3Status(jobId);
  return !mock && !!jobId && !!status && status.status !== 'CONFIRMED';
};

/**
 * 진행 중 배치의 "최종 일괄 등록 대상 수" — 상단 배너 "N개 리뷰에 답변을 등록하고 있어요"용.
 * job 단위 총량(고정)이라, 별점/플랫폼 필터로 실시간 감소하는 미답변 탭 숫자(reviews_summary)와
 * **의도적으로 분리**된 값이다(기획 합의: 필터 숫자=실시간 / 탭 숫자=최종 일괄등록 숫자).
 * active-status(V3StreamSubscriber가 이미 마운트한 공유 쿼리)만 읽어 추가 요청 없음.
 * 진행 중(비-terminal, 살아있는 job)이 아니면 null → 배너는 기존 실시간 미답변 문구로 폴백.
 */
export const useActiveBatchReplyTotal = (): number | null => {
  const mock = isIslandMockEnabled();
  const { data: active } = useBatchReplyV3ActiveStatus(!mock);
  const jobId = active?.job_id ?? null;
  if (mock || !active || !jobId || isDeadBatchJob(jobId)) return null;
  if (isTerminalV3Status(active.status)) return null;
  return active.stats?.total ?? null;
};

// ============================================
// 확인 처리
// ============================================
export const useConfirmBatchReplyV3 = (
  options?: MutationOpts<BatchReplyV3CreateResult, string>,
) => {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [...batchReplyV3Keys.all, 'confirm'],
    mutationFn: (jobId: string) => makeApi(accessToken).confirm(jobId),
    ...options,
    onSettled: (...args) => {
      queryClient.invalidateQueries({ queryKey: batchReplyV3Keys.all });
      options?.onSettled?.(...args);
    },
  });
};

export type BatchReplyV3LiveStatus = 'connecting' | 'streaming' | 'polling';

const BACKSTOP_MS = 18000; // SSE 정상 중에도 저빈도로 snapshot reconcile (놓친 delta 흡수)
const STATS_DEBOUNCE_MS = 800; // delta 버스트 후 집계 stats 확정 revalidate
const POLL_FALLBACK_MS = 3000; // SSE 끊김 지속 시 폴링 자가강등 주기(GET /snapshot). 복구되면 승격
// #3 서킷브레이커: 비-404 snapshot 에러가 이 횟수만큼 연속되면 폴링·재연결을 끊는다(무한 루프 방지)
const MAX_SNAPSHOT_ERRORS = 3;

/**
 * 진행상황 실시간 채널 단일 소유자 (AFFiNE RealtimeLiveQuery 2-레이어).
 * - 조정 레이어: RealtimeLiveQuery — ready/revalidate → GET /snapshot → applySnapshot.
 * - 트랜스포트: EventSourceConnection + share() — URL당 연결 1개(refCount) 보장, 자동 재연결.
 *   (AFFiNE SocketConnection 패턴 — 중복 마운트/StrictMode 이중 effect에도 EventSource 1개)
 *
 * SSE 프레임 = { changed: [{reply_id, review_id, request_status, state}] } delta.
 *  → 리뷰 카드 캐시(request_status)를 setQueryData로 국소 patch (목록 refetch 없음).
 * 집계 stats/최종 정합은 snapshot revalidate(연결/재연결/18s 백스톱)로 확정.
 */
export const useBatchReplyV3Live = (
  jobId: string | null,
  onGone?: () => void,
): BatchReplyV3LiveStatus => {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const enqueueIslandEvents = useEnqueueIslandEvents();
  const [status, setStatus] = useState<BatchReplyV3LiveStatus>('connecting');

  // 재연결/공유 연결이 항상 최신 토큰을 읽도록 stable ref (share()가 첫 인스턴스를 재사용해도 OK)
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;
  const onGoneRef = useRef(onGone);
  onGoneRef.current = onGone;

  useEffect(() => {
    // 죽은(404 확인된) job은 구독/폴링/재연결 자체를 열지 않는다 — 무한 404 방지
    if (!jobId || !accessToken || isDeadBatchJob(jobId)) return;
    const queryKey = batchReplyV3Keys.status(jobId);

    // 재연결 ready 신호 전용 스트림
    const events$ = new Subject<RealtimeSubscriptionReady>();

    // job이 사라짐(404) / 서버 error 프레임 → 죽은 것으로 표시하고 완전 정리(재폴링·재연결·재구독 중단) +
    // 상위(V3StreamSubscriber)가 stale jobId를 clear하도록 onGone 통지 + BE message를 toast로 1회 노출.
    // (dead-set 가드로 job당 toast 1회 — 새로고침·재구독 반복 스팸 방지)
    const handleGone = () => {
      const wasDead = isDeadBatchJob(jobId);
      markDeadBatchJob(jobId);
      teardown(true); // gone = 이 job은 끝 → force-close로 AutoReconnect 되살아나지 못하게
      onGoneRef.current?.();
      if (!wasDead) toast.error(JOB_GONE_MESSAGE);
    };

    // revalidate = 경량 snapshot fetch → status 캐시(집계 stats) 갱신 + items[] 카드 reconcile
    const detailKey = batchReplyV3Keys.detail(jobId);
    let snapshotErrorCount = 0; // #3 서킷브레이커용 연속 비-404 에러 카운터
    const applySnapshot = (snap: BatchReplyV3SnapshotDto) => {
      snapshotErrorCount = 0; // snapshot 성공 → 서킷브레이커 카운터 리셋
      queryClient.setQueryData(queryKey, snap); // snapshot ⊇ status
      reconcileFromSnapshot(queryClient, snap.items ?? []);
      reconcileDetailFromSnapshot(queryClient, detailKey, snap.items ?? []); // 현황 화면 fallback 보정
      // 완료(terminal) 확정 → force-close. BE가 스트림을 닫으며 발생한 예약 재연결까지 확실히 중단.
      if (isTerminalV3Status(snap.status)) teardown(true);
    };

    const liveQuery = new RealtimeLiveQuery<BatchReplyV3SnapshotDto, RealtimeSubscriptionReady>({
      request: (signal) => makeApi(accessTokenRef.current).getSnapshot(jobId, signal),
      subscribe: () => events$,
      applySnapshot,
      onError: (err) => {
        // snapshot이 404면 그 job은 존재X/소유권 불일치 → gone 처리(친화적 카피 노출)
        if (isNotFoundError(err)) {
          handleGone();
          return;
        }
        // #3 서킷브레이커: 비-404 에러(BE 장애/네트워크)가 연속 누적되면 폴링·재연결을 끊는다.
        // (기존엔 비-404를 무시 → teardown 없이 무한 폴링/재연결이 지속됐음)
        snapshotErrorCount += 1;
        if (snapshotErrorCount >= MAX_SNAPSHOT_ERRORS) teardown(true);
      },
    });

    // delta 버스트 후 집계 stats 확정용 디바운스 revalidate (delta마다 GET 방지)
    let statsTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleStatsRevalidate = () => {
      if (statsTimer) clearTimeout(statsTimer);
      statsTimer = setTimeout(() => {
        statsTimer = null;
        liveQuery.revalidate();
        // 진행 중 헤더 집계(미답변 탭/배너 숫자)도 함께 실시간화. reviews_summary는 rating/platform/date
        // 필터가 적용된 값이라, 4점 필터를 걸면 unReplied가 곧 "4점 미답변"이 되어 자동으로 스코프된다.
        // (완료 때 1회만 정합하면 진행 내내 숫자가 얼어붙어 "카드는 달리는데 16 고정" 혼란이 생겼음.
        //  같은 800ms 디바운스에 묶어 delta마다 GET 하지 않도록 유지 — 배치 진행 중에만 발생, bounded.)
        queryClient.invalidateQueries({ queryKey: ['reviews_summary_q'] });
      }, STATS_DEBOUNCE_MS);
    };

    // --- 트랜스포트 레이어 (share() 공유 SSEConnection: fetch + Bearer 헤더) ---
    let closed = false;
    // URL당 연결 1개 보장 (중복 마운트/StrictMode 방어 + 미래 SSE 기능 대비)
    const conn = share(
      new SSEConnection(batchReplyV3StreamUrl(jobId), () => accessTokenRef.current),
    );

    // 파싱된 SSE data(문자열) 처리 — delta patch + island 큐 + stats revalidate
    const handleMessage = (data: string) => {
      let frame: unknown;
      try {
        frame = JSON.parse(data);
      } catch {
        return;
      }
      if (!frame || typeof frame !== 'object') return;
      const f = frame as { event?: string; changed?: BatchReplyV3Delta[]; status?: string };
      if (f.event === 'error') {
        // 서버 SSE error 프레임(예: "찾을 수 없습니다") → gone 처리(친화적 카피, 재구독 loop 중단)
        handleGone();
        return;
      }
      if (Array.isArray(f.changed)) {
        f.changed.forEach((d) => {
          patchReviewReplyStatus(queryClient, d); // /main 리뷰 카드
          patchDetailReplyStatus(queryClient, detailKey, d); // 현황/결과 화면(라이브)
        });
        // 완료/실패만 island 활동 피드에 (진행중/pending은 제외)
        enqueueIslandEvents(
          f.changed
            .filter(
              (d): d is BatchReplyV3Delta & { review_id: string } =>
                !!d.review_id && (d.state === 'completed' || d.state === 'failed'),
            )
            .map((d) => ({ reviewId: d.review_id, state: d.state as 'completed' | 'failed' })),
        );
        scheduleStatsRevalidate();
        return;
      }
      // #1 스트림 프레임이 snapshot(status 필드)이면 직접 적용 → 종료 판정을 별도 GET에 의존하지 않는다.
      // BE가 terminal 스냅샷을 스트림의 첫/마지막 프레임으로 이미 보내므로(cmong-be sse-stream.util
      // `takeWhile((f)=>!f._terminal, true)`), 'connected' 경합·revalidate 실패·백그라운드 탭과
      // 무관하게 이 한 프레임으로 applySnapshot→teardown(true)이 걸려 재연결 루프가 끊긴다.
      // (AFFiNE RealtimeLiveQuery applyEvent가 이벤트 프레임에서 즉시 종료 판정하는 것과 동일 역할)
      if (typeof f.status === 'string') {
        applySnapshot(frame as BatchReplyV3SnapshotDto);
        return;
      }
      // (하위호환) status/changed 없는 프레임 → snapshot 재조회로 최신화
      liveQuery.revalidate();
    };

    // 메시지 구독은 연결 수명과 무관하게 1회 (SSEConnection이 재연결에도 emitter 유지)
    const offMessage = conn.onMessage(handleMessage);

    // --- 폴링 자가강등 (AFFiNE self-hosted 폴백 등가물) ---
    // SSE가 열려 있으면 push 우선. 연결이 error(끊김)로 떨어지면 GET /snapshot 주기 폴링으로
    // 강등(status='polling')하고, 재연결(connected)되면 폴링 중단 후 push로 승격(status='streaming').
    // AFFiNE은 Socket.IO transport 레벨 long-polling으로 폴백하지만, 우리 트랜스포트는 SSE라
    // 애플리케이션 레벨 snapshot 폴링으로 등가 동작을 만든다. revalidate는 generation/abort 가드가
    // 있어 push와 겹쳐도 안전.
    let pollTimer: ReturnType<typeof setInterval> | null = null;
    const startPolling = () => {
      if (pollTimer || closed) return;
      setStatus('polling');
      liveQuery.revalidate(); // 강등 즉시 1회 (다음 tick까지 안 기다림)
      pollTimer = setInterval(() => {
        if (!closed && document.visibilityState === 'visible') liveQuery.revalidate();
      }, POLL_FALLBACK_MS);
    };
    const stopPolling = () => {
      if (pollTimer) clearInterval(pollTimer);
      pollTimer = null;
    };

    const offStatus = conn.onStatusChanged((s) => {
      if (s === 'connected') {
        stopPolling(); // push 복구 → 폴링 해제, 승격
        setStatus('streaming');
        events$.next({ type: 'ready' }); // (재)연결 → revalidate로 최신 snapshot 보장
      } else if (s === 'error') {
        // 트랜스포트가 재연결을 포기한 실패(만료 토큰 401/403 등)면 폴링도 열지 않는다 —
        // 같은 죽은 토큰으로 GET /snapshot을 두드려도 결과는 같은 403이다. 토큰이 갱신되면
        // accessToken이 바뀌어 이 effect가 재실행되고 새 연결이 만들어져 회복된다.
        if (conn.retriesExhausted) return;
        // 일시적 실패/끊김(AutoReconnect가 3s 후 재시도) → 그 사이 폴링으로 진행 유지
        startPolling();
      } else if (s === 'closed') {
        // 정상 완료(end sentinel) / 재연결 포기 → 남은 폴링 타이머까지 확실히 정리
        stopPolling();
      }
    });

    // 최후의 보루: SSE 정상 중에도 탭이 보일 때 저빈도로 snapshot reconcile
    const backstopTimer = setInterval(() => {
      if (!closed && document.visibilityState === 'visible') {
        liveQuery.revalidate();
      }
    }, BACKSTOP_MS);

    // force=true면 refCount 무시하고 즉시 close(=refCount 0 → 'closed' → AutoReconnect 중단).
    // terminal/gone처럼 "이 job은 끝, 되살리지 마라"엔 force. 일반 unmount(effect cleanup)는 refCount--.
    function teardown(force = false) {
      closed = true;
      if (statsTimer) clearTimeout(statsTimer);
      statsTimer = null;
      stopPolling();
      clearInterval(backstopTimer);
      offMessage();
      offStatus();
      conn.disconnect(force); // refCount-- (0이면 실제 close/abort). force면 즉시 0 → 'closed'
      liveQuery.dispose();
      events$.complete();
    }

    liveQuery.start();
    conn.connect(); // refCount++ (첫 참조면 실제 fetch 시작)
    if (conn.status === 'connected') {
      setStatus('streaming');
      events$.next({ type: 'ready' }); // 이미 공유 연결이 열려있으면 즉시 ready
    }

    return teardown;
  }, [jobId, accessToken, queryClient, enqueueIslandEvents]);

  return status;
};

export type { BatchReplyV3StatusDto };
